// Types
import { Driver } from "@/types/supabase";

// Libs
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toastHandler } from "@/lib/ToastHandler";

// Hooks
import { useDriversApi } from "./useDriversApi";

const formSchema = z.object({
  name: z
    .string()
    .min(1, "El nombre es requerido")
    .max(30, "El nombre no debe exceder 30 caracteres")
    .regex(/^[A-Za-z\s]+$/, "El nombre solo debe contener letras"),
  email: z.string().email("Email inválido"),
  address: z
    .string()
    .min(1, "La dirección es requerida")
    .max(100, "La dirección no debe exceder 100 caracteres"),
  document: z
    .string()
    .min(1, "El documento es requerido")
    .max(20, "El documento no debe exceder 20 caracteres"),
  phone: z
    .string()
    .min(1, "El teléfono es requerido")
    .max(20, "El teléfono no debe exceder 20 caracteres")
    .regex(/^\d+$/, "El teléfono solo debe contener números"),
  vehicle_type: z.enum(["bicycle", "moto", "car"], {
    required_error: "El tipo de vehículo es requerido",
  }),
  branch_id: z.string().min(1, "La sucursal es requerida"),
  worker_type: z.enum(["full-time", "part-time", "freelance"], {
    required_error: "El tipo de contrato es requerido",
  }),
});

export type FormValues = z.infer<typeof formSchema>;

interface SavedFormData {
  formData: FormValues;
  timestamp: number;
  driverName: string;
}

interface UseDriverFormProps {
  driver: Driver;
  setDriver: (driver: Driver) => void;
  onUpdate?: (updateData: any) => void;
}

export function useDriverForm({
  driver,
  setDriver,
  onUpdate,
}: UseDriverFormProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialFormData, setInitialFormData] = useState<FormValues | null>(
    null,
  );
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [savedFormData, setSavedFormData] = useState<SavedFormData | null>(
    null,
  );

  const { updateDriver } = useDriversApi();

  // Helper function to normalize vehicle type values
  const normalizeVehicleType = (value: string): "bicycle" | "moto" | "car" => {
    if (value === "motorcycle") return "moto";
    if (value === "bicycle" || value === "moto" || value === "car") {
      return value as "bicycle" | "moto" | "car";
    }
    return "bicycle"; // default fallback
  };

  // Helper function to normalize worker type values
  const normalizeWorkerType = (
    value: string,
  ): "full-time" | "part-time" | "freelance" => {
    if (
      value === "full-time" ||
      value === "part-time" ||
      value === "freelance"
    ) {
      return value as "full-time" | "part-time" | "freelance";
    }
    return "full-time"; // default fallback
  };

  const getFormDefaults = () => ({
    name: driver?.name || "",
    email: driver?.email || "",
    address: driver?.address || "",
    document: driver?.document || "",
    phone: driver?.phone || "",
    vehicle_type: normalizeVehicleType(driver?.vehicle_type || "bicycle"),
    branch_id: driver?.branch_id ? String(driver.branch_id) : "",
    worker_type: normalizeWorkerType(driver?.worker_type || "full-time"),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: getFormDefaults(),
  });

  // LocalStorage utilities
  const getLocalStorageKey = () => `driver-form-${driver.id}`;

  const saveFormToLocalStorage = (formData: FormValues) => {
    try {
      const dataToSave: SavedFormData = {
        formData,
        timestamp: Date.now(),
        driverName: driver.name || `Driver ${driver.id}`,
      };
      localStorage.setItem(getLocalStorageKey(), JSON.stringify(dataToSave));
    } catch (error) {
      console.error("Error saving form to localStorage:", error);
    }
  };

  const loadFormFromLocalStorage = (): SavedFormData | null => {
    try {
      const saved = localStorage.getItem(getLocalStorageKey());
      if (saved) {
        return JSON.parse(saved) as SavedFormData;
      }
    } catch (error) {
      console.error("Error loading form from localStorage:", error);
    }
    return null;
  };

  const clearFormFromLocalStorage = () => {
    try {
      localStorage.removeItem(getLocalStorageKey());
    } catch (error) {
      console.error("Error clearing form from localStorage:", error);
    }
  };

  // Check for saved form data on component mount
  useEffect(() => {
    const savedData = loadFormFromLocalStorage();
    if (savedData) {
      setSavedFormData(savedData);
      setShowResumeDialog(true);
    }
  }, [driver.id]);

  // Sync form when driver data changes (but not when editing)
  useEffect(() => {
    if (!isEditing) {
      const newDefaults = getFormDefaults();
      form.reset(newDefaults);
    }
  }, [driver, isEditing]);

  // Watch form changes and save to localStorage when editing
  useEffect(() => {
    if (isEditing && !isLoading) {
      const subscription = form.watch((data) => {
        if (data && Object.keys(data).length > 0) {
          saveFormToLocalStorage(data as FormValues);
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [isEditing, isLoading, form]);

  const handleEdit = () => {
    // Use the same normalization logic for initial data
    const currentFormData = getFormDefaults();

    // Reset form to ensure consistent state
    form.reset(currentFormData);
    setInitialFormData(currentFormData);
    setIsEditing(true);
    saveFormToLocalStorage(currentFormData);
  };

  const handleCancel = () => {
    if (initialFormData) {
      form.reset(initialFormData);
    }
    setIsEditing(false);
  };

  const handleSave = async (data: FormValues) => {
    try {
      setIsLoading(true);

      // Prepare update data - only include fields that have changed
      const updateData: any = {};
      let hasChanges = false;

      if (initialFormData) {
        Object.keys(data).forEach((key) => {
          const formKey = key as keyof FormValues;
          const currentValue = data[formKey];
          const initialValue = initialFormData[formKey];

          if (currentValue !== initialValue) {
            if (formKey === "branch_id") {
              updateData[formKey] = Number(currentValue);
            } else {
              updateData[formKey] = currentValue;
            }
            hasChanges = true;
          }
        });
      }

      if (hasChanges) {
        const driverId =
          typeof driver.id === "string" ? parseInt(driver.id, 10) : driver.id;
        if (!driverId || isNaN(driverId)) {
          throw new Error(`Invalid driver ID: ${driver.id}`);
        }

        await updateDriver(driverId, updateData);

        // Update the driver with the new data
        const updatedDriver = { ...driver, ...updateData };
        setDriver(updatedDriver);
        onUpdate?.(updateData);

        // Create new defaults with the updated driver data directly
        const newDefaults = {
          name: updatedDriver?.name || "",
          email: updatedDriver?.email || "",
          address: updatedDriver?.address || "",
          document: updatedDriver?.document || "",
          phone: updatedDriver?.phone || "",
          vehicle_type: normalizeVehicleType(
            updatedDriver?.vehicle_type || "bicycle",
          ),
          branch_id: updatedDriver?.branch_id
            ? String(updatedDriver.branch_id)
            : "",
          worker_type: normalizeWorkerType(
            updatedDriver?.worker_type || "full-time",
          ),
        };

        form.reset(newDefaults);

        toastHandler({
          description: "Datos actualizados correctamente",
          variant: "success",
        });
      }

      clearFormFromLocalStorage();
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving data:", error);
      toastHandler({
        title: "Error",
        description: "No se pudieron guardar los cambios. Intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResumeEditing = () => {
    if (savedFormData) {
      form.reset(savedFormData.formData);
      setIsEditing(true);
      // Use the same normalization logic for consistency
      setInitialFormData(getFormDefaults());
    }
    setShowResumeDialog(false);
    setSavedFormData(null);
  };

  const handleDiscardSavedData = () => {
    clearFormFromLocalStorage();
    setShowResumeDialog(false);
    setSavedFormData(null);
  };

  return {
    form,
    isEditing,
    isLoading,
    showResumeDialog,
    savedFormData,
    handleEdit,
    handleCancel,
    handleSave,
    handleResumeEditing,
    handleDiscardSavedData,
  };
}
